import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { IntervalUnit } from '@/proto/logiblocks/logiblocks';
import {
  amountToSeconds,
  secondsToAmount,
} from '@/pages/Logiblocks/intervalUnits';
import styles from '../NodeCustomization.module.css';

const UNIT_OPTIONS = [
  { value: IntervalUnit.SECONDS, label: 'Seconds' },
  { value: IntervalUnit.MINUTES, label: 'Minutes' },
  { value: IntervalUnit.HOURS, label: 'Hours' },
];

/** The largest unit that shows `seconds` as a whole number. */
export function unitForSeconds(seconds: number): IntervalUnit {
  if (seconds > 0 && seconds % 3600 === 0) return IntervalUnit.HOURS;
  if (seconds > 0 && seconds % 60 === 0) return IntervalUnit.MINUTES;
  return IntervalUnit.SECONDS;
}

interface WaitDurationFieldProps {
  /** Total seconds; 0 shows an empty amount (the caller's default applies). */
  seconds: number;
  /** The unit to show; defaults to the largest one that keeps a whole number. */
  unit?: IntervalUnit;
  /** The unit to start in while the amount is empty; defaults to Seconds. */
  emptyUnit?: IntervalUnit;
  /** Reports total seconds and the unit the amount was entered in. */
  onChange: (seconds: number, unit: IntervalUnit) => void;
  disabled?: boolean;
  /** Shown in the empty amount box, e.g. "1 hour (default)". */
  placeholder?: string;
  /** Accessible name for the amount box. */
  amountLabel: string;
}

/**
 * Whole-number amount plus a Seconds / Minutes / Hours unit, stored as total seconds.
 * Clearing the amount reports 0.
 */
export const WaitDurationField: React.FC<WaitDurationFieldProps> = ({
  seconds,
  unit: savedUnit,
  emptyUnit = IntervalUnit.SECONDS,
  onChange,
  disabled = false,
  placeholder,
  amountLabel,
}) => {
  const [unit, setUnit] = useState(
    () => savedUnit ?? (seconds > 0 ? unitForSeconds(seconds) : emptyUnit)
  );

  // Follow outside changes (undo, a different node selected) without fighting typing.
  useEffect(() => {
    if (!Number.isInteger(secondsToAmount(seconds, unit))) {
      setUnit(unitForSeconds(seconds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  const amount = seconds > 0 ? secondsToAmount(seconds, unit) : '';

  const handleAmountChange = (raw: string) => {
    const value = Math.floor(Number(raw));
    onChange(
      raw === '' || !(value > 0) ? 0 : amountToSeconds(value, unit),
      unit
    );
  };

  const handleUnitChange = (newUnit: IntervalUnit) => {
    setUnit(newUnit);
    onChange(
      seconds > 0
        ? amountToSeconds(secondsToAmount(seconds, unit), newUnit)
        : 0,
      newUnit
    );
  };

  return (
    <div className={styles.inlineFields}>
      <input
        type='number'
        min={1}
        step={1}
        value={amount}
        placeholder={placeholder}
        aria-label={amountLabel}
        onChange={(e) => handleAmountChange(e.target.value)}
        className={`${styles.lbNumberInput} ${styles.inlineField}`}
        disabled={disabled}
      />
      <Select
        value={unit}
        onChange={handleUnitChange}
        disabled={disabled}
        style={{ flex: 1 }}
        className='agentSelect agentSelect--darkText'
        aria-label={`${amountLabel} unit`}
      >
        {UNIT_OPTIONS.map((opt) => (
          <Select.Option key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};
